import {
  Button,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Upload,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import {
  useGetAllPartnerCategoriesQuery,
  useGetAllPlansQuery,
  useUploadFileMutation,
} from "../graphql/generated/hooks";
import { useForm } from "antd/es/form/Form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import RobinPage from "../components/RobinPage";
import { useEffect, useState } from "react";
import type { UploadFile } from "antd";
import { gql, useApolloClient, useMutation, useQuery } from "@apollo/client";
import ResizableCropModal from "../components/ResizableCropModal";

const GET_PARTNER_QUERY = gql`
  query GetPartner($id: Int!) {
    getPartner(id: $id) {
      id
      name
      nameAr
      email
      phone
      address
      isActive
      image {
        id
        url
        originalName
      }
      coverImage {
        id
        url
        originalName
      }
      productImage {
        id
        url
        originalName
      }
      partnerCategories {
        id
      }
      plan {
        id
      }
    }
  }
`;

const CREATE_PARTNER_MUTATION = gql`
  mutation CreatePartner(
    $address: String
    $email: String
    $name: String!
    $nameAr: String!
    $phone: String
    $planId: Int!
    $partnerCategoryIds: [Int!]!
    $imageId: Int
    $coverImageId: Int
    $productImageId: Int
    $isActive: Boolean
  ) {
    createPartner(
      createPartnerDto: {
        address: $address
        email: $email
        name: $name
        nameAr: $nameAr
        phone: $phone
        planId: $planId
        partnerCategoryIds: $partnerCategoryIds
        imageId: $imageId
        coverImageId: $coverImageId
        productImageId: $productImageId
        isActive: $isActive
      }
    ) {
      id
    }
  }
`;

const UPDATE_PARTNER_MUTATION = gql`
  mutation UpdatePartner(
    $id: Int!
    $address: String
    $email: String
    $name: String
    $nameAr: String
    $phone: String
    $planId: Int
    $partnerCategoryIds: [Int!]
    $imageId: Int
    $coverImageId: Int
    $productImageId: Int
    $isActive: Boolean
  ) {
    updatePartner(
      id: $id
      updatePartnerDto: {
        address: $address
        email: $email
        name: $name
        nameAr: $nameAr
        phone: $phone
        planId: $planId
        partnerCategoryIds: $partnerCategoryIds
        imageId: $imageId
        coverImageId: $coverImageId
        productImageId: $productImageId
        isActive: $isActive
      }
    ) {
      id
    }
  }
`;

function PartnerMutate() {
  const { t, i18n } = useTranslation();
  const [form] = useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [ready, setReady] = useState<boolean>(false);
  const [partnerId, setPartnerId] = useState<number | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadedImageId, setUploadedImageId] = useState<number | null>(null);
  const [coverFileList, setCoverFileList] = useState<UploadFile[]>([]);
  const [uploadedCoverImageId, setUploadedCoverImageId] = useState<
    number | null
  >(null);
  const [productFileList, setProductFileList] = useState<UploadFile[]>([]);
  const [uploadedProductImageId, setUploadedProductImageId] = useState<
    number | null
  >(null);

  // Crop modal state (same flow as partner-portal SettingsPartnerInfo)
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageUrl, setCropImageUrl] = useState("");
  const [cropImageKind, setCropImageKind] = useState<
    "image" | "cover" | "product"
  >("image");
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const [createPartner, { loading: creating }] = useMutation(
    CREATE_PARTNER_MUTATION,
  );
  const [updatePartner, { loading: updating }] = useMutation(
    UPDATE_PARTNER_MUTATION,
  );
  const [uploadFile, { loading: uploading }] = useUploadFileMutation();
  const { data: partnerCategories, loading: loadingPartnerCategories } =
    useGetAllPartnerCategoriesQuery();
  const { data: plans, loading: loadingPlans } = useGetAllPlansQuery();
  const partnerCategoriesOptions =
    partnerCategories?.getAllPartnerCategories?.map((partnerCategory) => ({
      label: partnerCategory.name,
      value: partnerCategory.id,
    }));

  const parsedPartnerId =
    params.id && !Number.isNaN(Number(params.id)) ? Number(params.id) : null;

  const isEditRoute = !!parsedPartnerId;

  const {
    data: partnerData,
    loading: loadingPartner,
    error: partnerError,
  } = useQuery(GET_PARTNER_QUERY, {
    variables: parsedPartnerId ? { id: parsedPartnerId } : undefined,
    // Always fetch on /partners/mutate/:id so you can see the request in Network.
    // (Even if we also have location.state.partner from navigation.)
    skip: !isEditRoute,
    // Force a real network request so you can see it in DevTools Network
    fetchPolicy: "network-only",
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (!partnerError) return;
    messageApi.error(
      partnerError.message || t("message.error.data_fetch_failed"),
    );
    // eslint-disable-next-line
  }, [partnerError]);

  /*
    NOTE:
    - If we got here via PartnerList, we already have partner in location.state.
      In that case we skip the query.
    - If user opens /partners/mutate/:id directly, we fetch it by id.
  */

  const effectivePartner = partnerData?.getPartner || location.state?.partner;

  useEffect(() => {
    if (!params.id) {
      setEditMode(false);
      setPartnerId(null);
      setReady(true);
      return;
    }

    // Edit mode
    if (parsedPartnerId) {
      setEditMode(true);
      setPartnerId(parsedPartnerId);
    } else {
      messageApi.error(t("message.error.invalid_id"));
      navigate(-1);
    }
    // eslint-disable-next-line
  }, [params.id]);

  useEffect(() => {
    if (!editMode) return;

    if (loadingPartner) return;

    if (!effectivePartner) {
      messageApi.error(t("message.error.data_fetch_failed"));
      navigate(-1);
      return;
    }

    const phoneVal = effectivePartner.phone || "";
    let prefix = "+966";
    let phoneWithoutPrefix = phoneVal;

    const knownPrefixes = ["+20", "+966", "+971"];
    for (const p of knownPrefixes) {
      if (phoneVal.startsWith(p)) {
        prefix = p;
        phoneWithoutPrefix = phoneVal.substring(p.length);
        break;
      }
    }

    // Populate form fields
    form.setFieldsValue({
      ...effectivePartner,
      prefix,
      phone: phoneWithoutPrefix,
      partnerCategoryIds: effectivePartner.partnerCategories?.map(
        (partnerCategory: any) => partnerCategory.id,
      ),
      planId: effectivePartner.plan?.id,
    });

    // Set existing image if available
    if (effectivePartner.image) {
      setUploadedImageId(effectivePartner.image.id);
      setFileList([
        {
          uid: "-1",
          name: effectivePartner.image.originalName || "Partner Image",
          status: "done",
          url: effectivePartner.image.url,
        },
      ]);
    }

    // Set existing cover image if available
    if (effectivePartner.coverImage) {
      setUploadedCoverImageId(effectivePartner.coverImage.id);
      setCoverFileList([
        {
          uid: "-2",
          name:
            effectivePartner.coverImage.originalName || "Partner Cover Image",
          status: "done",
          url: effectivePartner.coverImage.url,
        },
      ]);
    }

    // Set existing product image if available
    if (effectivePartner.productImage) {
      setUploadedProductImageId(effectivePartner.productImage.id);
      setProductFileList([
        {
          uid: "-3",
          name:
            effectivePartner.productImage.originalName ||
            "Partner Product Image",
          status: "done",
          url: effectivePartner.productImage.url,
        },
      ]);
    }

    setReady(true);
    // eslint-disable-next-line
  }, [editMode, loadingPartner, effectivePartner]);

  // Convert file to base64
  const convertFileToBase64 = (file: File | Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
  };

  const handleFileSelect =
    (kind: "image" | "cover" | "product") => (file: File) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target?.result as string;
        setCropImageUrl(imageUrl);
        setCropImageKind(kind);
        setPendingFile(file);
        setCropModalOpen(true);
      };
      reader.readAsDataURL(file);
      return false; // Prevent default upload
    };

  const handleCropComplete = async (croppedBlob: Blob) => {
    try {
      const base64String = await convertFileToBase64(croppedBlob);
      const fileName = pendingFile?.name || `cropped-${Date.now()}.jpg`;

      const result = await uploadFile({
        variables: {
          base64File: base64String,
          originalName: fileName,
          folder:
            cropImageKind === "cover"
              ? "partners/covers"
              : cropImageKind === "product"
                ? "partners/products"
                : "partners",
        },
      });

      if (!result.data?.uploadFile) {
        throw new Error("Upload failed: No data returned");
      }

      const uploaded = result.data.uploadFile;

      if (cropImageKind === "cover") {
        setUploadedCoverImageId(uploaded.id);
        setCoverFileList([
          {
            uid: `-cover-${uploaded.id}`,
            name: fileName,
            status: "done",
            url: uploaded.url,
          },
        ]);
      } else if (cropImageKind === "product") {
        setUploadedProductImageId(uploaded.id);
        setProductFileList([
          {
            uid: `-product-${uploaded.id}`,
            name: fileName,
            status: "done",
            url: uploaded.url,
          },
        ]);
      } else {
        setUploadedImageId(uploaded.id);
        setFileList([
          {
            uid: `-image-${uploaded.id}`,
            name: fileName,
            status: "done",
            url: uploaded.url,
          },
        ]);
      }

      setCropModalOpen(false);
      setPendingFile(null);
      setCropImageUrl("");
      messageApi.success(t("message.success.file_uploaded"));
    } catch (error) {
      console.error("Upload error:", error);
      messageApi.error(t("message.error.file_upload_failed"));
      setCropModalOpen(false);
      setPendingFile(null);
      setCropImageUrl("");
    }
  };

  const handleEditImage = async (kind: "image" | "cover" | "product") => {
    let imageUrl = "";
    if (kind === "image" && fileList[0]?.url) {
      imageUrl = String(fileList[0].url);
    } else if (kind === "cover" && coverFileList[0]?.url) {
      imageUrl = String(coverFileList[0].url);
    } else if (kind === "product" && productFileList[0]?.url) {
      imageUrl = String(productFileList[0].url);
    }

    if (!imageUrl) return;

    setCropImageKind(kind);

    // For remote URLs: fetch and convert to data URL so cropping works (no tainted canvas).
    // If fetch fails (e.g. no CORS), we still open with URL so user can view; Done will show a warning.
    if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
      try {
        const res = await fetch(imageUrl, {
          mode: "cors",
          credentials: "omit",
        });
        if (!res.ok) throw new Error("Fetch failed");
        const blob = await res.blob();
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        setCropImageUrl(dataUrl);
        setCropModalOpen(true);
      } catch {
        setCropImageUrl(imageUrl);
        setCropModalOpen(true);
      }
    } else {
      setCropImageUrl(imageUrl);
      setCropModalOpen(true);
    }
  };

  const uploadButton = (
    <button style={{ border: 0, background: "none" }} type="button">
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>{t("button.upload")}</div>
    </button>
  );

  const handleValidationErrors = (error: any) => {
    // Check if error has GraphQL validation errors structure
    if (error.graphQLErrors && error.graphQLErrors.length > 0) {
      const gqlError = error.graphQLErrors[0];

      // Check for validation errors in extensions
      if (
        gqlError.extensions?.code === "VALIDATION_ERROR" &&
        gqlError.extensions?.errors
      ) {
        const validationErrors = gqlError.extensions.errors;

        // Set field-level errors in the form
        const formErrors = validationErrors.map((err: any) => ({
          name: err.field,
          errors: err.errors,
        }));

        form.setFields(formErrors);

        // Show general error message
        messageApi.error(
          gqlError.message || t("message.error.validation_failed"),
        );
        return;
      }
    }

    // If not a validation error, show generic error
    const errorMessage = editMode
      ? t("message.error.data_update_failed")
      : t("message.error.data_create_failed");
    messageApi.error(errorMessage);
    console.error(error);
  };

  const onFinish = async (values: any) => {
    const fullPhone =
      values.prefix && values.phone
        ? `${values.prefix}${values.phone}`
        : values.phone;

    if (editMode)
      await updatePartner({
        variables: {
          id: partnerId || location.state?.partner?.id,
          name: values.name,
          nameAr: values.nameAr,
          email: values.email,
          phone: fullPhone,
          address: values.address,
          partnerCategoryIds: values.partnerCategoryIds,
          planId: values.planId,
          imageId: uploadedImageId,
          coverImageId: uploadedCoverImageId,
          productImageId: uploadedProductImageId,
          isActive: values.isActive,
        },
        // Ensure screens that rely on cached data (like PartnerList) update without manual refresh
        refetchQueries: [
          "GetAllPartnersTable",
          "GetAllPartnersSelect",
          ...(partnerId
            ? [{ query: GET_PARTNER_QUERY, variables: { id: partnerId } }]
            : []),
        ],
        awaitRefetchQueries: true,
      })
        .then(() => {
          messageApi.success(t("message.success.data_updated"));
          apolloClient.refetchQueries({ include: ["GetAllPartnersTable"] });
          navigate(-1);
        })
        .catch((error) => {
          handleValidationErrors(error);
        });
    else
      await createPartner({
        variables: {
          name: values.name,
          nameAr: values.nameAr,
          email: values.email,
          phone: fullPhone,
          address: values.address,
          partnerCategoryIds: values.partnerCategoryIds,
          planId: values.planId,
          imageId: uploadedImageId,
          coverImageId: uploadedCoverImageId,
          productImageId: uploadedProductImageId,
          isActive: values.isActive,
        },
        refetchQueries: ["GetAllPartnersTable", "GetAllPartnersSelect"],
        awaitRefetchQueries: true,
      })
        .then(() => {
          messageApi.success(t("message.success.data_created"));
          apolloClient.refetchQueries({ include: ["GetAllPartnersTable"] });
          navigate(-1);
        })
        .catch((error) => {
          handleValidationErrors(error);
        });
  };

  return (
    <RobinPage
      title={editMode ? t("title.edit_partner") : t("title.add_partner")}
    >
      <Row>
        {contextHolder}
        <Col span={24}>
          {ready ? (
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              dir={i18n.dir()}
              name="mutatePartner"
            >
              <Form.Item
                name="name"
                label={t("label.partner.name")}
                rules={[
                  { required: true, message: t("message.error.field_required") },
                  {
                    validator: async (_, value) => {
                      if (typeof value === "string" && value.trim().length > 0)
                        return;
                      throw new Error(t("message.error.field_required"));
                    },
                  },
                ]}
                required
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="nameAr"
                label={t("label.partner.name_ar")}
                rules={[
                  { required: true, message: t("message.error.field_required") },
                  {
                    validator: async (_, value) => {
                      if (typeof value === "string" && value.trim().length > 0)
                        return;
                      throw new Error(t("message.error.field_required"));
                    },
                  },
                ]}
                required
              >
                <Input />
              </Form.Item>
              <Form.Item name="email" label={t("label.email")}>
                <Input type="email" />
              </Form.Item>
              <Form.Item
                name="phone"
                label={t("label.phone")}
                rules={[
                  {
                    required: true,
                    message: t("message.error.field_required"),
                  },
                  {
                    validator: async (_, value) => {
                      const phone = String(value ?? "").trim();
                      if (!phone) return;

                      // Allow typing anything numeric; validate format on blur/submit
                      if (phone[0] !== "5") {
                        throw new Error(t("message.error.phone_must_start_with_5"));
                      }

                      if (!/^\d{9}$/.test(phone)) {
                        throw new Error(t("message.error.phone_required"));
                      }
                    },
                  },
                ]}
              >
                <Input
                  addonBefore={
                    <Form.Item name="prefix" noStyle initialValue="+966">
                      <Select style={{ width: 110 }}>
                        <Select.Option value="+966">🇸🇦 +966</Select.Option>
                      </Select>
                    </Form.Item>
                  }
                  inputMode="numeric"
                  maxLength={9}
                  onBeforeInput={(event) => {
                    // Keep digits-only without using deprecated onKeyPress.
                    // (Validation rules handle "must start with 5".)
                    const inputEvent = event.nativeEvent as InputEvent;
                    const inserted = inputEvent.data ?? "";
                    if (inserted && !/^\d+$/.test(inserted)) {
                      event.preventDefault();
                    }
                  }}
                  onPaste={(event) => {
                    const pasted = event.clipboardData.getData("text");
                    if (pasted && !/^\d+$/.test(pasted)) {
                      event.preventDefault();
                    }
                  }}
                  style={{ width: "100%" }}
                  dir="ltr"
                />
              </Form.Item>
              <Form.Item name="address" label={t("label.address")}>
                <Input />
              </Form.Item>
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item name="imageId" label={t("label.partner.image")}>
                    <Upload
                      listType="picture-card"
                      fileList={fileList}
                      beforeUpload={(file) => handleFileSelect("image")(file)}
                      onChange={({ fileList: newFileList }) => {
                        if (
                          newFileList[0]?.status === "done" ||
                          newFileList.length === 0
                        ) {
                          setFileList(newFileList);
                        }
                      }}
                      onRemove={() => {
                        setUploadedImageId(null);
                        setFileList([]);
                      }}
                      onPreview={() => handleEditImage("image")}
                      maxCount={1}
                      accept="image/*"
                    >
                      {fileList.length >= 1 ? null : uploadButton}
                    </Upload>
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="coverImageId"
                    label={t("label.partner.cover_image")}
                  >
                    <Upload
                      listType="picture-card"
                      fileList={coverFileList}
                      beforeUpload={(file) => handleFileSelect("cover")(file)}
                      onChange={({ fileList: newFileList }) => {
                        if (
                          newFileList[0]?.status === "done" ||
                          newFileList.length === 0
                        ) {
                          setCoverFileList(newFileList);
                        }
                      }}
                      onRemove={() => {
                        setUploadedCoverImageId(null);
                        setCoverFileList([]);
                      }}
                      onPreview={() => handleEditImage("cover")}
                      maxCount={1}
                      accept="image/*"
                    >
                      {coverFileList.length >= 1 ? null : uploadButton}
                    </Upload>
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="productImageId"
                    label={t("label.partner.product_image")}
                  >
                    <Upload
                      listType="picture-card"
                      fileList={productFileList}
                      beforeUpload={(file) => handleFileSelect("product")(file)}
                      onChange={({ fileList: newFileList }) => {
                        if (
                          newFileList[0]?.status === "done" ||
                          newFileList.length === 0
                        ) {
                          setProductFileList(newFileList);
                        }
                      }}
                      onRemove={() => {
                        setUploadedProductImageId(null);
                        setProductFileList([]);
                      }}
                      onPreview={() => handleEditImage("product")}
                      maxCount={1}
                      accept="image/*"
                    >
                      {productFileList.length >= 1 ? null : uploadButton}
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                name="partnerCategoryIds"
                label={t("label.partner.category")}
                required
              >
                <Select mode="multiple" loading={loadingPartnerCategories}>
                  {partnerCategoriesOptions?.map((partnerCategory) => (
                    <Select.Option
                      key={partnerCategory.value}
                      value={partnerCategory.value}
                    >
                      {partnerCategory.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="planId" label={t("label.partner.plan")} required>
                <Select loading={loadingPlans}>
                  {plans?.getAllPlans?.map((plan) => (
                    <Select.Option key={plan.id} value={plan.id}>
                      {plan.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button
                    onClick={() => {
                      navigate(-1);
                    }}
                  >
                    {t("button.cancel")}
                  </Button>
                  <Form.Item noStyle shouldUpdate>
                    {() => {
                      const name = (form.getFieldValue("name") ?? "").trim();
                      const nameAr = (form.getFieldValue("nameAr") ?? "").trim();
                      const isBusy = creating || updating || uploading;
                      const disableSubmit = isBusy || !name || !nameAr;

                      return (
                        <Button
                          loading={isBusy}
                          disabled={disableSubmit}
                          type="primary"
                          htmlType="submit"
                        >
                          {t("button.save")}
                        </Button>
                      );
                    }}
                  </Form.Item>
                </Space>
              </Form.Item>
            </Form>
          ) : (
            <Spin />
          )}
        </Col>
      </Row>

      <ResizableCropModal
        open={cropModalOpen}
        imageUrl={cropImageUrl}
        shape={
          cropImageKind === "cover"
            ? "rectangle"
            : cropImageKind === "product"
              ? "square"
              : null
        }
        onCancel={() => {
          setCropModalOpen(false);
          setPendingFile(null);
          setCropImageUrl("");
        }}
        onOk={handleCropComplete}
      />
    </RobinPage>
  );
}

export default PartnerMutate;
